import { useCallback, MouseEvent } from 'react';

/**
 * Returns an `onMouseEnter` handler that sets the element's `title` attribute
 * to its own text content when the content is visually truncated — either
 * horizontally (`text-overflow: ellipsis`) or vertically (`line-clamp`). Lets
 * the browser show the full content as a native tooltip on hover. The check
 * runs lazily on hover to avoid measuring thousands of virtualized cells
 * up-front.
 */
export function useTruncationTitle<T extends HTMLElement>() {
  return useCallback((event: MouseEvent<T>) => {
    const el = event.currentTarget;
    const isTruncated =
      el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
    if (isTruncated) {
      const text = el.innerText ?? el.textContent ?? '';
      if (text && el.getAttribute('title') !== text) {
        el.setAttribute('title', text);
      }
    } else if (el.hasAttribute('title')) {
      el.removeAttribute('title');
    }
  }, []);
}
